import React, { useState, useEffect } from 'react';
import './ListaPracownikow.css';
import rotationsAPI from '../../api/rotations';

function ListaPracownikow({ users, projects, contracts, positions, onUpdateUser, onDeleteUser, currentUser }) {
  // Debug: sprawdź czy onDeleteUser jest przekazany
  useEffect(() => {
    if (!onDeleteUser) {
      console.warn('onDeleteUser is not provided to ListaPracownikow component');
    }
  }, [onDeleteUser]);
  // Projekty dostępne dla zalogowanego użytkownika
  const getAvailableProjects = () => {
    if (currentUser?.role === 'owner') {
      return projects; // Owner widzi wszystkie projekty
    }
    // Admin i Lider widzą tylko projekty, do których są przypisani
    return currentUser?.projects || [];
  };

  const availableProjects = getAvailableProjects();
  
  // Filtruj tylko pracowników (role: 'employee')
  const employees = users.filter(user => user.role === 'employee');
  
  // Grupuj pracowników według projektów
  const employeesByProject = {};
  
  availableProjects.forEach(project => {
    employeesByProject[project] = employees.filter(emp => 
      emp.projects && emp.projects.includes(project)
    );
  });

  // Pracownicy bez przypisanego projektu
  const employeesWithoutProject = employees.filter(emp => 
    !emp.projects || emp.projects.length === 0
  );

  const [selectedProject, setSelectedProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rotations, setRotations] = useState([]);
  const [editSelectedProject, setEditSelectedProject] = useState('');

  const isSuperAdmin = currentUser?.role === 'owner';
  const isAdmin = currentUser?.role === 'admin';
  const canEditDelete = isSuperAdmin || isAdmin;

  // Wczytaj rotacje dla wybranego projektu w edycji
  useEffect(() => {
    const loadRotations = async () => {
      if (!editSelectedProject) {
        setRotations([]);
        return;
      }
      
      try {
        const rotationsData = await rotationsAPI.getByProject(editSelectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          project: r.project || editSelectedProject
        }));
        setRotations(formattedRotations);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
      }
    };
    
    loadRotations();
  }, [editSelectedProject]);

  const handleProjectClick = (project) => {
    setSelectedProject(selectedProject === project ? null : project);
  };

  const getTotalEmployees = () => {
    return employees.length;
  };

  // Pobierz dostępne rotacje dla wybranego projektu
  const getAvailableRotations = (project = null) => {
    const projectToUse = project || editSelectedProject;
    if (!projectToUse) return [];
    
    // Użyj rotacji załadowanych w useEffect
    return rotations.map(rotation => ({
      id: rotation.id,
      title: rotation.title,
      displayName: `${rotation.title} (Rota. ${rotation.rotationNumber})`
    }));
  };

  // Pobierz dostępne umowy dla wybranego projektu
  const getAvailableContracts = (project = null) => {
    const projectToUse = project || editSelectedProject;
    if (!projectToUse) return [];
    return contracts[projectToUse] || [];
  };

  // Pobierz dostępne stanowiska dla wybranego projektu
  const getAvailablePositions = (project = null) => {
    const projectToUse = project || editSelectedProject;
    if (!projectToUse) return [];
    return positions[projectToUse] || [];
  };

  // Pobierz listę liderów z użytkowników
  const getAvailableLeaders = () => {
    if (!users) return [];
    return users.filter(u => u.role === 'lider' && u.email && !u.email.includes('_'));
  };

  // Funkcje do edycji pracownika
  const handleEditEmployee = async (employee) => {
    setEditingEmployee(employee);
    
    // Wczytaj rotacje dla projektu pracownika
    const employeeProject = employee.projects?.[0] || employee.project || '';
    let employeeRotations = [];
    if (employeeProject) {
      try {
        const rotationsData = await rotationsAPI.getByProject(employeeProject);
        employeeRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          project: r.project || employeeProject
        }));
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
      }
    }
    
    // Znajdź rotację pracownika
    const employeeRotation = employeeRotations.find(r => 
      r.id === String(employee.rotationId) || r.title === employee.shift
    );
    
    setEditFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      position: employee.position || '',
      shift: employeeRotation ? String(employeeRotation.id) : '',
      contract: employee.contract || '',
      employmentType: employee.employmentType || '',
      assignedUser: employee.systemUser || '',
      leader: employee.leader || '',
      project: employeeProject
    });
    
    setEditSelectedProject(employeeProject);
    setShowEditModal(true);
    setError('');
    setSuccess('');
  };

  const handleEditChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    if (name === 'project') {
      setEditSelectedProject(value);
      setEditFormData({
        ...editFormData,
        project: value,
        shift: '',
        contract: '',
        position: ''
      });
    } else {
      setEditFormData({
        ...editFormData,
        [name]: value
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.firstName || !editFormData.lastName || !editFormData.position || !editFormData.project || !editFormData.shift || !editFormData.contract || !editFormData.assignedUser) {
      setError('Proszę wypełnić wszystkie wymagane pola');
      return;
    }

    // Wczytaj rotacje dla właściwego projektu
    const projectForValidation = editFormData.project;
    let rotationsForProject = [];
    if (projectForValidation) {
      try {
        const rotationsData = await rotationsAPI.getByProject(projectForValidation);
        rotationsForProject = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          project: r.project || projectForValidation
        }));
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
      }
    }
    
    const shiftId = editFormData.shift ? String(editFormData.shift) : null;
    const selectedRotationData = rotationsForProject.find(r => String(r.id) === shiftId);
    
    if (!selectedRotationData && editFormData.shift) {
      setError('Wybrana rotacja nie istnieje dla tego projektu');
      return;
    }

    // Sprawdź czy umowa istnieje
    const availableContracts = getAvailableContracts(editFormData.project);
    if (!availableContracts.includes(editFormData.contract)) {
      setError('Wybrana umowa nie istnieje dla tego projektu');
      return;
    }

    // Sprawdź czy stanowisko istnieje
    const availablePositions = getAvailablePositions(editFormData.project);
    if (!availablePositions.includes(editFormData.position)) {
      setError('Wybrane stanowisko nie istnieje dla tego projektu');
      return;
    }

    const rotationTitle = selectedRotationData ? selectedRotationData.title : editFormData.shift;
    const fullName = `${editFormData.firstName} ${editFormData.lastName}`;

    // Sprawdź czy inny pracownik o tym samym imieniu, nazwisku i projekcie już istnieje (oprócz edytowanego)
    const existingEmployee = users.find(u => 
      u.email !== editingEmployee.email &&
      u.firstName === editFormData.firstName && 
      u.lastName === editFormData.lastName && 
      u.projects && u.projects.includes(editFormData.project)
    );
    
    if (existingEmployee) {
      setError('Pracownik o tym imieniu i nazwisku jest już przypisany do tego projektu');
      return;
    }

    onUpdateUser(editingEmployee.email, {
      name: fullName,
      firstName: editFormData.firstName,
      lastName: editFormData.lastName,
      position: editFormData.position,
      projects: [editFormData.project],
      shift: rotationTitle,
      rotationId: editFormData.shift,
      contract: editFormData.contract,
      employmentType: editFormData.employmentType || '',
      systemUser: editFormData.assignedUser,
      leader: editFormData.leader || '',
      projectShift: { project: editFormData.project, shift: rotationTitle, rotationId: editFormData.shift }
    });

    setSuccess('Pracownik został zaktualizowany pomyślnie!');
    setShowEditModal(false);
    setEditingEmployee(null);
    setEditFormData(null);
    setEditSelectedProject('');
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingEmployee(null);
    setEditFormData(null);
    setEditSelectedProject('');
    setError('');
  };

  // Funkcje do usuwania pracownika
  const handleDeleteEmployee = (employee) => {
    console.log('handleDeleteEmployee called with:', employee);
    if (!employee) {
      console.error('Employee is null or undefined');
      setError('Brak danych pracownika');
      return;
    }
    // Pracownicy są identyfikowani przez id, nie email
    if (!employee.id) {
      console.error('Employee id is missing:', employee);
      setError('Brak identyfikatora pracownika (id)');
      return;
    }
    setDeletingEmployee(employee);
    setShowDeleteModal(true);
    setError('');
  };

  const handleDeleteConfirm = () => {
    console.log('handleDeleteConfirm called', { deletingEmployee, onDeleteUser: !!onDeleteUser });
    
    if (!deletingEmployee) {
      setError('Brak danych pracownika');
      console.error('deletingEmployee is null or undefined');
      return;
    }
    
    if (!onDeleteUser) {
      setError('Funkcja usuwania nie jest dostępna');
      console.error('onDeleteUser is not defined');
      return;
    }
    
    // Pracownicy są identyfikowani przez id, nie email
    const employeeId = deletingEmployee.id;
    if (!employeeId) {
      setError('Brak identyfikatora pracownika (id)');
      console.error('Employee id is missing:', deletingEmployee);
      return;
    }
    
    try {
      console.log('Calling onDeleteUser with id:', employeeId);
      // Przekaż id pracownika - funkcja onDeleteUser obsługuje zarówno email jak i id
      onDeleteUser(employeeId, deletingEmployee.role === 'employee');
      setSuccess('Pracownik został usunięty pomyślnie!');
      setShowDeleteModal(false);
      setDeletingEmployee(null);
      setError(''); // Wyczyść błędy jeśli były
    } catch (error) {
      setError('Wystąpił błąd podczas usuwania pracownika');
      console.error('Error deleting employee:', error);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingEmployee(null);
  };

  return (
    <div className="lista-pracownikow-container">
      <div className="lista-pracownikow-header">
        <div className="stats">
          <span className="stat-item">
            <strong>Łączna liczba pracowników:</strong> {getTotalEmployees()}
          </span>
        </div>
      </div>
      
      {error && <div className="admin-message error">{error}</div>}
      {success && <div className="admin-message success">{success}</div>}

      {availableProjects.length === 0 ? (
        <div className="no-projects-message">
          <p>Brak dostępnych projektów. Nie masz przypisanych projektów.</p>
        </div>
      ) : (
        <div className="projects-list">
          {availableProjects.map((project, index) => {
            const projectEmployees = employeesByProject[project] || [];
            const isExpanded = selectedProject === project;
            
            return (
              <div key={index} className="project-section">
                <div 
                  className="project-header"
                  onClick={() => handleProjectClick(project)}
                >
                  <h3>{project}</h3>
                  <div className="project-info">
                    <span className="employee-count">
                      {projectEmployees.length} {projectEmployees.length === 1 ? 'pracownik' : 'pracowników'}
                    </span>
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="employees-table-container">
                    {projectEmployees.length === 0 ? (
                      <div className="no-employees">
                        <p>Brak pracowników przypisanych do tego projektu.</p>
                      </div>
                    ) : (
                      <table className="employees-table">
                        <thead>
                          <tr>
                            <th>Imię</th>
                            <th>Nazwisko</th>
                            <th>Stanowisko</th>
                            <th>Zmiana</th>
                            <th>Umowa</th>
                            {canEditDelete && <th>Akcje</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {projectEmployees.map((employee, empIndex) => (
                            <tr key={employee.id || employee.email || empIndex}>
                              <td>{employee.firstName || employee.name?.split(' ')[0] || '-'}</td>
                              <td>{employee.lastName || employee.name?.split(' ').slice(1).join(' ') || '-'}</td>
                              <td>{employee.position || '-'}</td>
                              <td>
                                {employee.shift ? (
                                  <span className="shift-badge">{employee.shift}</span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td>{employee.contract || '-'}</td>
                              {canEditDelete && (
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      type="button"
                                      className="edit-button"
                                      onClick={() => handleEditEmployee(employee)}
                                      title="Edytuj pracownika"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      className="delete-button"
                                      onClick={() => {
                                        console.log('Delete button clicked for employee:', employee);
                                        handleDeleteEmployee(employee);
                                      }}
                                      title="Usuń pracownika"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {employeesWithoutProject.length > 0 && (
            <div className="project-section">
              <div 
                className="project-header"
                onClick={() => handleProjectClick('no-project')}
              >
                <h3>Bez przypisanego projektu</h3>
                <div className="project-info">
                  <span className="employee-count">
                    {employeesWithoutProject.length} {employeesWithoutProject.length === 1 ? 'pracownik' : 'pracowników'}
                  </span>
                  <span className="expand-icon">{selectedProject === 'no-project' ? '▼' : '▶'}</span>
                </div>
              </div>
              
              {selectedProject === 'no-project' && (
                <div className="employees-table-container">
                  <table className="employees-table">
                    <thead>
                      <tr>
                        <th>Imię</th>
                        <th>Nazwisko</th>
                        <th>Stanowisko</th>
                        <th>Zmiana</th>
                        <th>Umowa</th>
                        {canEditDelete && <th>Akcje</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {employeesWithoutProject.map((employee, empIndex) => (
                        <tr key={employee.id || employee.email || empIndex}>
                          <td>{employee.firstName || employee.name?.split(' ')[0] || '-'}</td>
                          <td>{employee.lastName || employee.name?.split(' ').slice(1).join(' ') || '-'}</td>
                          <td>{employee.position || '-'}</td>
                          <td>
                            {employee.shift ? (
                              <span className="shift-badge">{employee.shift}</span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{employee.contract || '-'}</td>
                          {canEditDelete && (
                            <td>
                              <div className="action-buttons">
                                <button
                                  type="button"
                                  className="edit-button"
                                  onClick={() => handleEditEmployee(employee)}
                                  title="Edytuj pracownika"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  className="delete-button"
                                  onClick={() => {
                                    console.log('Delete button clicked for employee:', employee);
                                    handleDeleteEmployee(employee);
                                  }}
                                  title="Usuń pracownika"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal dla edycji pracownika */}
      {showEditModal && editFormData && (
        <div className="modal-overlay" onClick={handleEditCancel}>
          <div className="modal-content edit-employee-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edytuj pracownika</h3>
            {error && <div className="admin-message error">{error}</div>}
            {success && <div className="admin-message success">{success}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="modal-form-section">
                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label htmlFor="edit-firstName">
                      Imię <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="edit-firstName"
                      name="firstName"
                      value={editFormData.firstName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="edit-lastName">
                      Nazwisko <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="edit-lastName"
                      name="lastName"
                      value={editFormData.lastName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                </div>
                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label htmlFor="edit-project">
                      Projekt <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="edit-project"
                      name="project"
                      value={editFormData.project}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Wybierz projekt</option>
                      {projects.map((project, idx) => (
                        <option key={idx} value={project}>{project}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="edit-position">
                      Stanowisko <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="edit-position"
                      name="position"
                      value={editFormData.position}
                      onChange={handleEditChange}
                      required
                      disabled={!editFormData.project}
                    >
                      <option value="">{editFormData.project ? 'Wybierz stanowisko' : 'Najpierw wybierz projekt'}</option>
                      {getAvailablePositions(editFormData.project).map((position, idx) => (
                        <option key={idx} value={position}>{position}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label htmlFor="edit-shift">
                      Rotacja <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="edit-shift"
                      name="shift"
                      value={editFormData.shift}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Wybierz rotację</option>
                      {getAvailableRotations(editFormData.project).map((rotation) => (
                        <option key={rotation.id} value={rotation.id}>{rotation.displayName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="edit-contract">
                      Typ umowy <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="edit-contract"
                      name="contract"
                      value={editFormData.contract}
                      onChange={handleEditChange}
                      required
                      disabled={!editFormData.project}
                    >
                      <option value="">{editFormData.project ? 'Wybierz umowę' : 'Najpierw wybierz projekt'}</option>
                      {getAvailableContracts(editFormData.project).map((contract, idx) => (
                        <option key={idx} value={contract}>{contract}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="edit-employmentType">
                      Rodzaj zatrudnienia
                    </label>
                    <select
                      id="edit-employmentType"
                      name="employmentType"
                      value={editFormData.employmentType}
                      onChange={handleEditChange}
                    >
                      <option value="">Wybierz rodzaj zatrudnienia</option>
                      <option value="Umowa o pracę">Umowa o pracę</option>
                      <option value="Umowa zlecenie">Umowa zlecenie</option>
                    </select>
                  </div>
                </div>
                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label htmlFor="edit-assignedUser">
                      Użytkownik systemu (np. SAP) <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="edit-assignedUser"
                      name="assignedUser"
                      value={editFormData.assignedUser}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="modal-form-group">
                    <label htmlFor="edit-leader">
                      Przypisz Lidera
                    </label>
                    <select
                      id="edit-leader"
                      name="leader"
                      value={editFormData.leader}
                      onChange={handleEditChange}
                    >
                      <option value="">Wybierz lidera (opcjonalnie)</option>
                      {getAvailableLeaders().map((leader) => (
                        <option key={leader.email} value={leader.email}>{leader.name} ({leader.email})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn-cancel"
                  onClick={handleEditCancel}
                >
                  Anuluj
                </button>
                <button 
                  type="submit"
                  className="btn-confirm"
                >
                  Zapisz zmiany
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal dla potwierdzenia usunięcia pracownika */}
      {showDeleteModal && deletingEmployee && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Potwierdź usunięcie</h3>
            <p>
              Czy na pewno chcesz usunąć pracownika{' '}
              <strong>
                {deletingEmployee.firstName || deletingEmployee.name?.split(' ')[0] || ''}{' '}
                {deletingEmployee.lastName || deletingEmployee.name?.split(' ').slice(1).join(' ') || ''}
              </strong>?
            </p>
            <p className="delete-warning">Ta operacja jest nieodwracalna.</p>
            <div className="modal-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={handleDeleteCancel}
              >
                Anuluj
              </button>
              <button 
                type="button"
                className="btn-delete"
                onClick={handleDeleteConfirm}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ListaPracownikow;



